import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import type { MainTab, ProductTab } from '@product-updates/shared-util';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { E2ETestDirective } from '../../directives';
import { ProductUpdatesStateService } from '../../services/product-updates-state.service';

@Component({
  selector: 'pu-mobile-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent, E2ETestDirective, TranslatePipe],
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.less'],
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class MobileMenuComponent {
  private readonly stateService = inject(ProductUpdatesStateService);

  private readonly translationService = inject(I18nService);

  // Inputs
  public readonly isOpen = input.required<boolean>();
  public readonly activeMainTab = input.required<MainTab>();
  public readonly activeProductTab = input.required<ProductTab>();
  public readonly currentLocale = input.required<string>();
  public readonly availableLocales = input.required<string[]>();
  public readonly isDarkMode = input.required<boolean>();

  // Outputs
  public readonly closeMenu = output<void>();
  public readonly mainTabChange = output<MainTab>();
  public readonly productTabChange = output<ProductTab>();
  public readonly localeChange = output<string>();
  public readonly themeToggle = output<void>();

  public readonly mainNavItems = computed<MainTab[]>(() => {
    const items: MainTab[] = ['whats-new', 'coming-soon', 'timeline'];
    if (this.stateService.hasChangelogPosts()) {
      // Insert changelog before timeline
      items.splice(2, 0, 'changelog');
    }
    return items;
  });
  public readonly productTabs: ProductTab[] = ['all', 'hire', 'manage', 'grow', 'ai'];

  public readonly currentLocaleLabel = computed(() => this.getLanguageLabel(this.currentLocale()));

  public getMainNavLabel(tab: MainTab): string {
    const keyMap: Record<MainTab, string> = {
      'whats-new': 'nav.whats_new',
      'coming-soon': 'nav.coming_soon',
      changelog: 'nav.changelog',
      timeline: 'nav.timeline',
    };
    return this.translationService.translate(keyMap[tab]);
  }

  public getProductTabLabel(tab: ProductTab): string {
    const keyMap: Record<ProductTab, string> = {
      all: 'filters.all',
      ai: 'filters.ai',
      hire: 'filters.hire',
      manage: 'filters.manage',
      grow: 'filters.grow',
    };
    return this.translationService.translate(keyMap[tab]);
  }

  public onBackdropClick(): void {
    this.closeMenu.emit();
  }

  public onPanelClick(event: Event): void {
    event.stopPropagation();
  }

  public onEscapeKey(): void {
    if (this.isOpen()) {
      this.closeMenu.emit();
    }
  }

  public onMainTabClick(tab: MainTab): void {
    this.mainTabChange.emit(tab);
  }

  public onProductTabClick(tab: ProductTab): void {
    this.productTabChange.emit(tab);
  }

  public onLocaleChange(event: Event): void {
    const select = <HTMLSelectElement>event.target;
    this.localeChange.emit(select.value);
  }

  public onThemeToggle(): void {
    this.themeToggle.emit();
  }

  public getLanguageLabel(locale: string): string {
    const keyMap: Record<string, string> = {
      en: 'language.en',
      de: 'language.de',
      fr: 'language.fr',
      nl: 'language.nl',
      fi: 'language.fi',
      sv: 'language.sv',
    };
    return this.translationService.translate(keyMap[locale]) || locale.toUpperCase();
  }
}
